import { useRef, useState } from "react";
import { Video, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";

const CHUNK = 4 * 1024 * 1024;
const MAX = 150 * 1024 * 1024;
const newId = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

const VideoUpload = ({ value, onChange }) => {
  const inputRef = useRef();
  const [progress, setProgress] = useState(null);

  const handle = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > MAX) { toast.error("Vídeo muito grande (máx. 150 MB)"); return; }
    const uploadId = newId();
    const total = Math.max(1, Math.ceil(file.size / CHUNK));
    setProgress(0);
    try {
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("index", String(i));
        fd.append("total", String(total));
        fd.append("filename", file.name);
        fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK), file.name);
        const r = await api.post("/admin/upload/chunk", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setProgress(Math.round(((i + 1) / total) * 100));
        if (r.data.done) onChange(r.data.url);
      }
      toast.success("Vídeo enviado");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao enviar vídeo");
    } finally {
      setProgress(null);
    }
  };

  return (
    <div data-testid="video-upload">
      <div className="flex items-start gap-3">
        {value ? (
          <div className="relative">
            <video src={fileUrl(value)} controls playsInline preload="metadata" className="w-28 aspect-[9/16] rounded-xl bg-black object-cover" data-testid="video-upload-preview" />
            <button type="button" onClick={() => onChange("")} data-testid="video-upload-remove" className="absolute -top-2 -right-2 bg-destructive text-white rounded-full p-1"><X className="w-3 h-3" /></button>
          </div>
        ) : (
          <button type="button" onClick={() => inputRef.current?.click()} disabled={progress !== null} data-testid="video-upload-btn"
            className="w-28 aspect-[9/16] rounded-xl border-2 border-dashed border-border flex flex-col items-center justify-center gap-1 text-muted-foreground hover:border-primary transition-colors">
            {progress !== null ? <Loader2 className="w-5 h-5 animate-spin" /> : <Video className="w-6 h-6" />}
            <span className="text-[11px] font-semibold">{progress !== null ? `${progress}%` : "Enviar vídeo"}</span>
          </button>
        )}
        <div className="flex-1 min-w-0">
          <button type="button" onClick={() => inputRef.current?.click()} disabled={progress !== null} className="text-sm font-semibold text-primary" data-testid="video-upload-change">
            {value ? "Trocar vídeo" : "Escolher vídeo (MP4, MOV, WEBM)"}
          </button>
          {progress !== null && (
            <div className="mt-2 h-2 rounded-full bg-muted overflow-hidden" data-testid="video-upload-progress">
              <div className="h-full bg-primary transition-[width] duration-300" style={{ width: `${progress}%` }} />
            </div>
          )}
          <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="ou cole o link de um arquivo .mp4"
            className="mt-2 w-full text-xs px-2 py-1.5 rounded-lg border border-border bg-background outline-none focus:border-primary" data-testid="video-upload-url" />
          <p className="text-xs text-muted-foreground mt-1">Ideal: vídeo vertical 9:16, até 150 MB.</p>
        </div>
      </div>
      <input ref={inputRef} type="file" accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.m4v,.webm" className="hidden" onChange={handle} />
    </div>
  );
};

export default VideoUpload;
